// Centralized role & track definitions for ACE Rotations frontend.
// Distinguish between platform roles (admin, super admin) and pathway tracks (np, pa, img)
// plus preceptor pathway variants. The internal Track values use 'pa' (Physician Assistant)
// but the URL path segments remain '/ap/' for backward compatibility with existing bookmarks.

export enum Role {
  StudentNP = 'student_np',
  StudentPA = 'student_pa',
  StudentIMG = 'student_img',
  PreceptorNP = 'preceptor_np',
  PreceptorPA = 'preceptor_pa',
  PreceptorIMG = 'preceptor_img',
  Admin = 'admin',
  SuperAdmin = 'super_admin',
}

export type Track = 'np' | 'pa' | 'img'; // Internal track key

export const STUDENT_TRACKS: Track[] = ['np', 'pa', 'img'];
export const STUDENT_ROLES: Role[] = [Role.StudentNP, Role.StudentPA, Role.StudentIMG];
export const PRECEPTOR_ROLES: Role[] = [Role.PreceptorNP, Role.PreceptorPA, Role.PreceptorIMG];

export interface RoleMeta {
  role: Role;
  track?: Track; // present for student & preceptor pathway roles
  category: 'student' | 'preceptor' | 'admin';
  label: string; // Human readable full label
  short: string; // Short label used for badges or minimal UI (IMG / NP / PA)
  pathSegment?: string; // URL segment used for routing (may differ from track for legacy URLs).
}

export const ROLE_META: RoleMeta[] = [
  { role: Role.StudentNP, track: 'np', category: 'student', label: 'Nurse Practitioner Student', short: 'NP', pathSegment: 'np' },
  { role: Role.StudentPA, track: 'pa', category: 'student', label: 'Physician Assistant Student', short: 'PA', pathSegment: 'ap' },
  { role: Role.StudentIMG, track: 'img', category: 'student', label: 'International Medical Graduate', short: 'IMG', pathSegment: 'img' },
  { role: Role.PreceptorNP, track: 'np', category: 'preceptor', label: 'Preceptor (NP Track)', short: 'Preceptor NP', pathSegment: 'np' },
  { role: Role.PreceptorPA, track: 'pa', category: 'preceptor', label: 'Preceptor (PA Track)', short: 'Preceptor PA', pathSegment: 'ap' },
  { role: Role.PreceptorIMG, track: 'img', category: 'preceptor', label: 'Preceptor (IMG Track)', short: 'Preceptor IMG', pathSegment: 'img' },
  { role: Role.Admin, category: 'admin', label: 'Administrator', short: 'Admin' },
  { role: Role.SuperAdmin, category: 'admin', label: 'Super Administrator', short: 'Super Admin' },
];

export function findRoleMetaByTrack(track: Track, category: 'student' | 'preceptor'): RoleMeta | undefined {
  return ROLE_META.find(r => r.track === track && r.category === category);
}

export function isStudentTrack(segment: string | undefined): segment is Track {
  return !!segment && STUDENT_TRACKS.includes(segment as Track);
}

export function roleMetaForStudentTrack(track: Track) {
  return findRoleMetaByTrack(track, 'student');
}

export function roleMetaForPreceptorTrack(track: Track) {
  return findRoleMetaByTrack(track, 'preceptor');
}

// Derive student role enum from track segment.
export function studentRoleFromTrack(track: Track): Role {
  switch (track) {
    case 'np': return Role.StudentNP;
    case 'pa': return Role.StudentPA;
    case 'img': return Role.StudentIMG;
  }
}

export function preceptorRoleFromTrack(track: Track): Role {
  switch (track) {
    case 'np': return Role.PreceptorNP;
    case 'pa': return Role.PreceptorPA;
    case 'img': return Role.PreceptorIMG;
  }
}

/**
 * Convert a URL path segment (/np, /ap, /img) into the internal Track key.
 * /ap/ remains the user-facing URL for the PA track to preserve existing bookmarks.
 */
export function trackFromPathSegment(segment: string | undefined): Track | undefined {
  if (!segment) return undefined;
  switch (segment) {
    case 'np': return 'np';
    case 'ap': return 'pa';
    case 'img': return 'img';
    default: return undefined;
  }
}
